import { Controller } from "@hotwired/stimulus"

// 드롭다운 컨트롤러
// 드롭다운 메뉴의 토글 기능을 제공합니다.
export default class extends Controller {
  static targets = ["menu"]
  static values = { open: Boolean }

  connect() {
    console.log("Dropdown controller connected")
    
    // 상태 초기화 - Turbo Drive 페이지 전환 시 안전하게 초기화
    this.initializeState()
    
    // 이벤트 리스너 설정
    this.setupEventListeners()
    
    // Turbo 캐시 전 정리 이벤트
    this.setupTurboEventListeners()
    
    console.log("Dropdown menu target found:", this.hasMenuTarget)
  }

  disconnect() {
    console.log("Dropdown controller disconnected")
    
    // 상태 정리
    this.cleanupState()
    
    // 모든 이벤트 리스너 제거
    this.removeEventListeners()
  }

  // 상태 초기화
  initializeState() {
    // DOM 상태와 내부 상태 동기화
    if (this.hasMenuTarget) {
      const isMenuHidden = this.menuTarget.classList.contains("hidden")
      this.openValue = !isMenuHidden
      
      // 초기 상태는 항상 닫힌 상태로 설정 (Turbo 페이지 전환 시 안정성)
      if (!isMenuHidden) {
        this.forceClose()
      }
    } else {
      this.openValue = false
    }
  }

  // 상태 정리
  cleanupState() {
    // 메뉴를 확실히 닫고 상태 정리
    this.forceClose()
  }

  // 이벤트 리스너 설정
  setupEventListeners() {
    // 기존 리스너가 있다면 제거 (방어 코드)
    if (this.clickOutsideHandler) {
      document.removeEventListener("click", this.clickOutsideHandler)
    }
    
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)
    
    // ESC 키로 드롭다운 닫기
    if (this._handleEscapeKey) {
      document.removeEventListener("keydown", this._handleEscapeKey)
    }
    this._handleEscapeKey = this.handleEscapeKey.bind(this)
    document.addEventListener("keydown", this._handleEscapeKey)
  }

  // Turbo 이벤트 리스너 설정
  setupTurboEventListeners() {
    if (this._handleBeforeCache) {
      document.removeEventListener("turbo:before-cache", this._handleBeforeCache)
    }
    this._handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this._handleBeforeCache)
  }

  // 모든 이벤트 리스너 제거
  removeEventListeners() {
    if (this.clickOutsideHandler) {
      document.removeEventListener("click", this.clickOutsideHandler)
      this.clickOutsideHandler = null
    }
    
    if (this._handleEscapeKey) {
      document.removeEventListener("keydown", this._handleEscapeKey)
      this._handleEscapeKey = null
    }
    
    if (this._handleBeforeCache) {
      document.removeEventListener("turbo:before-cache", this._handleBeforeCache)
      this._handleBeforeCache = null
    }
  }

  toggle(event) {
    event.stopPropagation()
    
    if (!this.hasMenuTarget) {
      console.error("Dropdown: menu target not found")
      return
    }
    
    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasMenuTarget || this.openValue) {
      return
    }
    
    console.log("Opening dropdown menu")
    this.openValue = true
    this.menuTarget.classList.remove("hidden")
    
    // 다른 드롭다운 닫기 (같은 페이지의 다른 드롭다운과 충돌 방지)
    this.closeOtherDropdowns()
  }

  close() {
    if (!this.hasMenuTarget || !this.openValue) {
      return
    }
    
    console.log("Closing dropdown menu")
    this.openValue = false
    this.menuTarget.classList.add("hidden")
  }

  // 애니메이션 없이 즉시 닫기 (Turbo 전환 시 사용)
  forceClose() {
    if (this.hasMenuTarget) {
      this.openValue = false
      this.menuTarget.classList.add("hidden")
      console.log("Dropdown force closed")
    }
  }

  // 같은 페이지의 다른 드롭다운 닫기
  closeOtherDropdowns() {
    const otherDropdowns = document.querySelectorAll('[data-controller*="dropdown"]')
    otherDropdowns.forEach(dropdown => {
      if (dropdown !== this.element) {
        const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown")
        if (controller && controller.openValue) {
          controller.close()
        }
      }
    })
  }

  // 외부 클릭 처리
  clickOutside(event) {
    if (!this.openValue || this.element.contains(event.target)) {
      return
    }
    
    console.log("Dropdown: outside click detected")
    this.close()
  }

  // ESC 키 처리
  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.openValue) {
      console.log("Dropdown: ESC key pressed")
      this.close()
    }
  }

  // Turbo 캐시 전 정리
  handleBeforeCache() {
    if (this.openValue) {
      console.log("Dropdown: closing before cache")
      this.forceClose()
    }
  }
}
